import { openExternal } from "@/lib/open-external";
import { Heart, LifeBuoy, Leaf, Mail, Phone, Stethoscope, Wind, X } from "lucide-react";
import { Link } from "@tanstack/react-router";

interface CrisisDialogProps {
  onClose: () => void;
  footerNote?: string;
}

/**
 * Mandatory safety message shown when potential crisis language is detected.
 * Recovery Trauma™ is not a crisis service — directs users to real-world support
 * and in-app calming tools without blocking them.
 */
export function CrisisDialog({ onClose, footerNote }: CrisisDialogProps) {
  return (
    <div
      className="fixed inset-0 z-[80] flex items-end justify-center bg-[#2a1e3f]/40 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="crisis-dialog-title"
    >
      <div
        className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl border border-[#d9c8f5]/70 bg-gradient-to-br from-white via-[#faf5fc] to-[#f3e7f7] text-[#2a1e3f] shadow-2xl sm:rounded-3xl dark:border-border dark:from-card dark:via-card dark:to-background dark:text-foreground"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-7 sm:px-8 sm:py-8">
          <div className="mb-4 flex items-center justify-between">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/80 px-3 py-1 text-xs font-medium text-[#7a5cb0]">
              <Heart className="h-3.5 w-3.5" />
              You are not alone
            </div>
            <button
              onClick={onClose}
              className="rounded-full p-1.5 text-muted-foreground hover:bg-black/5 hover:text-foreground dark:hover:bg-white/10"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <h2
            id="crisis-dialog-title"
            className="font-serif text-2xl text-[#2a1e3f] dark:text-foreground sm:text-3xl"
          >
            🤍 Your safety matters.
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-[#2a1e3f]/85 dark:text-foreground/90">
            <span className="font-semibold">Recovery Trauma™ is not a crisis service.</span>{" "}
            If you are in immediate danger, experiencing thoughts of self-harm,
            or require urgent support, contact local emergency services or an
            appropriate crisis service.
          </p>

          <div className="mt-6 space-y-3">
            <div className="rounded-2xl border border-[#d9c8f5]/70 bg-white/80 p-4 dark:border-border dark:bg-background/70">
              <div className="mb-1 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-[#7a5cb0]">
                <Phone className="h-3.5 w-3.5" />
                Crisis resources
              </div>
              <ul className="mt-2 space-y-2 text-sm text-[#2a1e3f]/90 dark:text-foreground/90">
                <li>
                  <span className="font-medium">US &amp; Canada:</span> Call or text{" "}
                  <a href="tel:988" className="font-semibold text-[#7a5cb0] underline underline-offset-2">
                    988
                  </a>{" "}
                  (Suicide &amp; Crisis Lifeline)
                </li>
                <li>
                  <span className="font-medium">UK &amp; ROI:</span> Call{" "}
                  <a href="tel:116123" className="font-semibold text-[#7a5cb0] underline underline-offset-2">
                    116 123
                  </a>{" "}
                  (Samaritans)
                </li>
                <li>
                  <span className="font-medium">Worldwide:</span>{" "}
                  <a
                    href="https://findahelpline.com"
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => openExternal("https://findahelpline.com", e)}
                    className="font-medium text-[#7a5cb0] underline underline-offset-2"
                  >
                    findahelpline.com
                  </a>
                </li>
                <li>
                  <span className="font-medium">Emergency:</span>{" "}
                  <a href="tel:911" className="underline underline-offset-2">911</a> (US),{" "}
                  <a href="tel:112" className="underline underline-offset-2">112</a> (EU),{" "}
                  <a href="tel:999" className="underline underline-offset-2">999</a> (UK)
                </li>
              </ul>
            </div>

            {/* In-app calming tools */}
            <div className="grid gap-2 sm:grid-cols-2">
              <SafetyAction to="/grounding" icon={Leaf} label="Grounding exercise" />
              <SafetyAction to="/breathing" icon={Wind} label="Breathing now" />
              <SafetyAction to="/therapists" icon={Stethoscope} label="Find a therapist" />
              <SafetyAction
                href="mailto:support@recoverytrauma.com?subject=I%20need%20support&body=Hi%20Recovery%20Trauma%20team%2C%20I%27m%20reaching%20out%20because%20I%27m%20struggling%20and%20could%20use%20some%20support."
                icon={Mail}
                label="Email Recovery Trauma™"
              />
            </div>
          </div>

          <div className="mt-6 flex flex-col-reverse items-stretch gap-2 sm:flex-row sm:justify-end">
            <button
              onClick={onClose}
              className="rounded-full px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground"
            >
              Continue
            </button>
            <Link
              to="/crisis"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#9b7cc7] to-[#c48bb8] px-5 py-2.5 text-sm font-medium text-white shadow-md transition-transform hover:scale-[1.02]"
            >
              <LifeBuoy className="h-4 w-4" />
              Open crisis support
            </Link>
          </div>

          {footerNote && (
            <p className="mt-5 text-center text-xs text-muted-foreground">
              {footerNote}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SafetyAction({
  to,
  href,
  icon: Icon,
  label,
}: {
  to?: string;
  href?: string;
  icon: typeof Heart;
  label: string;
}) {
  const className =
    "inline-flex items-center justify-center gap-2 rounded-2xl border border-[#d9c8f5]/70 bg-card/80 px-4 py-3 text-sm font-medium text-foreground transition hover:bg-card dark:border-[#9b7cc7]/40";
  const inner = (
    <>
      <Icon className="h-4 w-4 text-[#7a5cb0]" />
      {label}
    </>
  );
  if (href) {
    return (
      <a href={href} className={className}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to!} className={className}>
      {inner}
    </Link>
  );
}
